import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * ModuleDialog — shared wrapper for every module-scoped "creation" dialog.
 *
 * Visual system (Le Desk):
 *  • 2 px border in the module colour (yellow for Tâches, Word-blue for
 *    Documents, violet for Calendrier, etc.) — that's the ONLY module
 *    signal on the frame; the rest of the dialog stays on the standard
 *    dark card background.
 *  • Header sits on the normal dark background with the module icon (in
 *    module colour) inline next to the title. No filled banner — we
 *    tried a coloured banner earlier and it was too heavy visually.
 *  • Accessible: DialogDescription is filled in (silences Radix warning).
 *
 * Props
 *   open, onOpenChange     — Dialog controlled state.
 *   title                  — main heading (required).
 *   description            — sub-line under the title.
 *   moduleColor            — hex string; drives border + inline icon tint.
 *   moduleIcon             — Lucide icon element rendered next to the title.
 *   maxWidth               — optional override for the DialogContent max-w
 *                            class (defaults to "max-w-3xl").
 *   className              — extra classes for DialogContent.
 *   stickyContent          — optional node rendered between the header and
 *                            the scrollable body (also sticky — spec iter181,
 *                            2026-02-27, pour figer par ex. le bloc
 *                            RÉFÉRENCE/CRÉÉE LE pendant le défilement).
 *   footer                 — node rendered at the bottom (usually action btns).
 *   children               — the body.
 *   testId                 — data-testid on DialogContent.
 */
export default function ModuleDialog({
  open,
  onOpenChange,
  title,
  description = "",
  moduleColor,
  moduleIcon,
  maxWidth = "max-w-3xl",
  className = "",
  stickyContent = null,
  footer,
  children,
  testId = "module-dialog",
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => { const a = document.activeElement; if (a && /^(date|datetime-local|time|month)$/.test(a.type || "")) e.preventDefault(); }}
        className={`${maxWidth} max-h-[90vh] bg-card p-0 flex flex-col overflow-hidden ${className}`}
        style={{
          // Thin coloured accent on the LEFT edge only — the rest of the
          // frame stays flat. Same principle for every future module dialog:
          // pass the module colour in and you get its 3 px left-border.
          borderTopWidth: 0,
          borderRightWidth: 0,
          borderBottomWidth: 0,
          borderLeftWidth: 3,
          borderLeftStyle: "solid",
          borderLeftColor: moduleColor,
        }}
        data-testid={testId}
      >
        {/* Header — standard dark bg, module icon inline for identity.
            Spec iter181 (2026-02-27) — sticky : le container passe en
            `flex flex-col overflow-hidden`, l'en-tête et le bloc
            `stickyContent` reçoivent `shrink-0`, le corps `flex-1
            overflow-y-auto`. Pour aligner Tâches sur AVI (bandeau
            titre figé) et gagner le même comportement partout. */}
        <DialogHeader className="px-6 py-4 flex-row items-center gap-3 space-y-0 shrink-0" data-testid={`${testId}-header`}>
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${moduleColor}1F`, color: moduleColor, border: `1px solid ${moduleColor}66` }}
            data-testid={`${testId}-icon`}
          >
            {moduleIcon}
          </div>
          <div className="flex-1 min-w-0 space-y-0.5">
            <DialogTitle className="text-2xl leading-tight">{title}</DialogTitle>
            {description && (
              <DialogDescription className="text-muted-foreground">
                {description}
              </DialogDescription>
            )}
          </div>
        </DialogHeader>

        {/* Sticky content (optional) — rendered between header and body.
            Spec iter181 (2026-02-27) — pour le bandeau RÉFÉRENCE/CRÉÉE LE
            de Tâches qui doit rester visible pendant le scroll. */}
        {stickyContent && (
          <div className="px-6 pb-3 shrink-0" data-testid={`${testId}-sticky`}>
            {stickyContent}
          </div>
        )}

        {/* Body — seule zone qui scroll. */}
        <div className="px-6 pb-5 pt-1 flex-1 overflow-y-auto">{children}</div>

        {/* Footer (optional) — rendered outside the padded body so callers
             can pick their own spacing / alignment. */}
        {footer && (
          <div className="px-6 py-4 border-t border-vertec-borderDark bg-background/40 shrink-0">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
